import React, { useCallback, useState } from 'react';
import WalletBalanceHero, { WalletBalanceHeroProps } from './WalletBalanceHero';
import WalletWithdrawalSuccessModal from './WalletWithdrawalSuccessModal';
import WalletWithdrawals from './WalletWithdrawals';
import WalletPayoutDetailModal from './WalletPayoutDetailModal';
import WalletPayoutsButton from './WalletPayoutsButton';
import { useWalletWithdrawalSuccess } from '../hooks/useWalletWithdrawalSuccess';

export interface WalletWhopWithdrawSectionProps {
  heroProps: Omit<WalletBalanceHeroProps, 'payoutAction'>;
  companyId: string;
  accessToken: string;
  kycApproved: boolean | null;
  onKycApprovalChange: (approved: boolean) => void;
  influencerId?: string;
  hasWhopAccount: boolean;
  withdrawalsRefreshKey: number;
  onRefreshBalance: () => void;
  onRefreshWithdrawals: () => void;
}

const WalletWhopWithdrawSection: React.FC<WalletWhopWithdrawSectionProps> = ({
  heroProps,
  companyId,
  accessToken,
  kycApproved,
  onKycApprovalChange,
  influencerId,
  hasWhopAccount,
  withdrawalsRefreshKey,
  onRefreshBalance,
  onRefreshWithdrawals,
}) => {
  const [selectedPayoutId, setSelectedPayoutId] = useState<string | null>(null);

  const {
    isSuccessModalOpen,
    isDetailModalOpen,
    latestWithdrawalId,
    handleWithdrawalComplete,
    handleViewStatus,
    handleCloseSuccessModal,
    handleCloseDetailModal,
  } = useWalletWithdrawalSuccess({
    influencerId,
    onRefreshBalance,
    onRefreshWithdrawals,
    onViewWithdrawalsFallback: () => {
      document.getElementById('wallet-withdrawals')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  });

  const onWithdrawalClick = useCallback((withdrawalId: string) => {
    setSelectedPayoutId(withdrawalId);
  }, []);

  const closeDetailModal = useCallback(() => {
    setSelectedPayoutId(null);
    handleCloseDetailModal();
  }, [handleCloseDetailModal]);

  const payoutAction = (
    <WalletPayoutsButton
      companyId={companyId}
      accessToken={accessToken}
      influencerId={influencerId}
      kycApproved={kycApproved}
      onKycApprovalChange={onKycApprovalChange}
      onWithdrawalComplete={handleWithdrawalComplete}
    />
  );

  return (
    <>
      <WalletBalanceHero {...heroProps} payoutAction={payoutAction} />
      <WalletWithdrawals
        enabled={hasWhopAccount}
        refreshKey={withdrawalsRefreshKey}
        onWithdrawalClick={onWithdrawalClick}
      />
      <WalletWithdrawalSuccessModal
        isOpen={isSuccessModalOpen}
        onClose={handleCloseSuccessModal}
        onViewStatus={handleViewStatus}
      />
      <WalletPayoutDetailModal
        isOpen={isDetailModalOpen || selectedPayoutId !== null}
        payoutId={selectedPayoutId ?? latestWithdrawalId}
        influencerId={influencerId}
        onClose={closeDetailModal}
      />
    </>
  );
};

export default WalletWhopWithdrawSection;
